// Updated: 2026-08-14
//
// THE unread-notification-count accessor (react-query-notifications, Req 2).
//
// ─── WHAT THIS HOOK IS FOR NOW ────────────────────────────────────────────────────────────────────
//
// Before slice 6 the unread count had THREE implementations, two of them live in front of a user:
//
//   A. `UserRelationsContext.unreadNotificationCount` — set from the bulk `fetchUserRelations` payload,
//      fetched ONCE per `idToken` for the whole session behind a `lastFetchedIdToken` ref guard, and
//      never refreshed by a mark-read or a delete. Read by the nav bell's badge.
//   B. `NotificationCenter`'s own `getUnreadNotificationCount` effect behind a `countsLoaded` latch,
//      then hand-decremented on every write. Read by the five category badges.
//   C. THIS FILE — its own effect plus manual `refreshUnreadCount` / `decrementUnreadCount` /
//      `resetUnreadCount`. Read by NOBODY: `grep -rn 'useNotifications' services/ integration-tests/`
//      returned only this module's own declaration and default export.
//
// A and B are the SAME NUMBER at the moment of fetch — both read `user#{identity_id}` /
// `notification_unread_counter` / `unread_count` (`lambda-secure/src/routes/get-user-relations.ts:107-113`
// and `lambda-secure/src/services/notifications.ts:752-762`, which remaps it to `count`). They desync
// purely through fetch discipline: mark a notification read on `/notifications` and B's local number
// drops while A holds its session-open value, so the bell badge and the category badges disagree until a
// full reload.
//
// This hook is now the ONE consumer-facing view over the ONE cache entry
// `['me','notifications','unreadCount']`, and BOTH badge surfaces read it — `NotificationBell` for the
// total, `NotificationCenter` for the five per-category numbers. The two notification mutations are the
// entry's only invalidators, so a mark-read or a delete moves every badge in the app at once.
//
// Source A is GONE. Slice 6 could not remove `UserRelationsContext.unreadNotificationCount` — doing so
// meant rewriting the provider — so it left the field in place, read by no badge, with the cleanup list in
// `.kiro/specs/react-query-notifications/design.md` § D2. Slice 7 (react-query-user-relations) performed
// that rewrite and REMOVED the field, together with `notifications` and `notificationsLastKey` and the
// `UserRelationsContextType` declarations behind all three. The provider is now a derivation over
// `['me','relations']` and holds no notification state at all.
//
// THE RULE THAT REPLACED IT, and it is permanent: `['me','notifications','unreadCount']` is the ONLY source
// of an unread count. Never read one off a bulk payload again — that is precisely how A and B got out of
// step. A bulk payload is fetched on its own schedule (A's was once per session, behind a ref guard that no
// notification write could reach), so the count inside it goes stale the moment anything is read or
// deleted, and no amount of care at the read site can fix a number that arrives attached to unrelated data.
// A count needs its own entry because it needs its own INVALIDATORS. Anyone adding a badge later reads this
// hook; the relations payload exposes no count, the context type declares none, and there is no field left
// to reach for.
//
// ─── WHY `decrementUnreadCount` / `resetUnreadCount` ARE GONE ─────────────────────────────────────
//
// They existed to hand-maintain a PRIVATE COPY of a server-owned number, which is the desync mechanism
// in miniature. A consolidated entry refreshed by invalidation has nothing for them to do, and keeping
// them would leave a supported way to reintroduce exactly the defect this slice removed. Dropping them is
// provably safe — the hook had zero importers (Req 2.6).

import type { UnreadNotificationCountResponse } from '@mrb91002/shared-types'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { notificationKeys } from '../queries/notification/keys'
import { useUnreadNotificationCountQuery } from '../queries/notification/queries'

/**
 * Zeroed counts, used while the query is idle or pending.
 *
 * This reproduces the pre-migration initial render EXACTLY — `useState(0)` in this hook and
 * `useState({count: 0, patterns: 0, materials: 0, social: 0, system: 0})` in `NotificationCenter` — which
 * matters because both badge surfaces HIDE the badge at zero. A signed-out or still-loading caller
 * therefore renders no badge, as it always did.
 *
 * Frozen and module-level so it is referentially stable across renders.
 */
const ZERO_COUNTS: UnreadNotificationCountResponse = Object.freeze({
  count: 0,
  patterns: 0,
  materials: 0,
  social: 0,
  system: 0,
})

export const useNotifications = () => {
  const queryClient = useQueryClient()
  const { data, isPending, isFetching } = useUnreadNotificationCountQuery()

  /**
   * Force a re-read of the ONE count entry.
   *
   * An invalidation rather than a `refetch()` so it also reaches any other mounted consumer of the same
   * entry — which, after this slice, is the whole point: the bell and the Notification Center are both
   * subscribed to it.
   */
  const refreshUnreadCount = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
  }, [queryClient])

  const counts = data ?? ZERO_COUNTS

  return {
    /** Total unread across every category — the nav bell's badge. */
    unreadCount: counts.count,
    /** All five numbers — the Notification Center's per-category badges. */
    counts,
    /** True only while the FIRST load of the entry is in flight, matching the pre-migration `loading`. */
    loading: isPending && isFetching,
    refreshUnreadCount,
  }
}

export default useNotifications
